import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Collapse, Typography, Button } from "antd"
import { CaretLeftOutlined, PlusOutlined, ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons"
import HoverWrapper from "./HoverWrapper"
import { createFormValuesSlicer, formConditionsHandler, getAllWidgets, sectionValueDeps } from "../../../utils/formsHelper";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

const availableWidth = {
    "6": "25%",
    "8": "33.33%",
    "12": "50%",
    "16": "66.67%",
    "18": "75%",
    "24": "100%"
}

// Drop zone component for inserting elements between section elements
// See FormDropZone in FormBody.jsx - the hook is in the inner component so an idle
// canvas registers no insertion zones.
const SectionDropZone = (props) => {
    if (!props.isDraggingWidget) return null;
    return <SectionDropZoneInner {...props} />;
};

const SectionDropZoneInner = ({ position, elementId, sectionId }) => {
    const { t } = useTranslation();
    const { setNodeRef, isOver } = useDroppable({
        id: `section-insert-${position}-${elementId}`,
        data: {
            type: 'section-insertion-zone',
            position: position, // 'before' or 'after'
            targetElementId: elementId,
            sectionId: sectionId,
            accepts: ['widget']
        }
    });

    return (
        <div
            ref={setNodeRef}
            style={{
                position: "absolute",
                height: "20px",
                width: "100%",
                top: position === "before" ? "-10px" : "auto",
                bottom: position === "after" ? "-10px" : "auto",
                left: "0",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
                backgroundColor: isOver ? "rgba(24, 144, 255, 0.08)" : "transparent",
                borderRadius: "6px",
                cursor: "pointer",
                pointerEvents: "auto",
            }}
        >
            {/* Always show a subtle line when dragging */}
            <div
                style={{
                    width: isOver ? "90%" : "50%",
                    height: "2px",
                    backgroundColor: isOver ? "#1890ff" : "#e6f4ff",
                    transition: "all 0.3s ease",
                    opacity: isOver ? 1 : 0.5,
                    borderRadius: "1px",
                    boxShadow: isOver ? "0 0 6px rgba(24, 144, 255, 0.3)" : "none",
                }}
            />

            {/* The + icon that appears on hover */}
            {isOver && (
                <div
                    className="insertion-plus-icon"
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "24px",
                        height: "24px",
                        backgroundColor: "#1890ff",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: "16px",
                        fontWeight: "bold",
                        boxShadow: "0 3px 10px rgba(24, 144, 255, 0.4)",
                        zIndex: 1,
                        border: "2px solid white",
                    }}
                >
                    +
                </div>
            )}

            {/* Helper text on hover */}
            {isOver && (
                <div
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "calc(50% + 40px)",
                        transform: "translateY(-50%)",
                        backgroundColor: "#1890ff",
                        color: "white",
                        padding: "3px 6px",
                        borderRadius: "3px",
                        fontSize: "11px",
                        fontWeight: "500",
                        boxShadow: "0 2px 6px rgba(24, 144, 255, 0.3)",
                        zIndex: 2,
                        whiteSpace: "nowrap",
                    }}
                >
                    {t('Insert')} {position}
                </div>
            )}
        </div>
    );
};

// SortableItem component for individual draggable items within sections
// Memoized: a section holds hundreds of these, and without this every keystroke
// anywhere in the form re-rendered all of them.
const SortableItem = React.memo(({ id, data, index, sectionId, totalItems, activeDragItem, ...rest }) => {
    const { t } = useTranslation();
    const { userInfo } = useSelector(state => state.user);

    // Zones only exist for the section the pointer has entered - see SectionHoverContext.
    const sectionEntered = useContext(SectionHoverContext);
    const isDraggingWidget = sectionEntered && activeDragItem?.data?.current?.type === "widget";
    const isFirstElement = index === 0;

    const {
        attributes,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id,
        data: {
            type: 'section-element',
            elementId: id,
            parentSectionId: sectionId,
            formType: data.formType,
            originalData: data,
            index: index,
            label: data.label || data.formType,
            isSectionSortable: true
        },
        // Prevent dragging if disabled or if user is typing
        disabled: rest.previewMode || false,
        // Add activation constraint to prevent accidental drags
        activationConstraint: {
            distance: 8, // Require 8px of movement before starting drag
        }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        width: data.span ? availableWidth[data.span] : "100%",
        zIndex: isDragging ? 1000 : 'auto',
        pointerEvents: 'auto'
    };

    // Handle click events separately from drag events
    const handleElementClick = (event) => {
        if (!isDragging && rest.widgetClickHandler) {
            event.preventDefault();
            event.stopPropagation();
            rest.widgetClickHandler(data);
        }
    };

    // Handle drag start to prevent event bubbling
    const handleDragStart = (event) => {
        event.stopPropagation();
    };

    // Handle keyboard events to prevent interference with typing
    const handleKeyDown = (event) => {
        // Prevent space key and other keys from triggering drag operations
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.contentEditable === 'true') {
            event.stopPropagation();
        }
    };

    // Handle move up/down buttons
    const handleMoveUp = (event) => {
        event.stopPropagation();
        event.preventDefault();
        if (index > 0 && rest.handleSectionReorder) {
            rest.handleSectionReorder(sectionId, index, index - 1);
        }
    };

    const handleMoveDown = (event) => {
        event.stopPropagation();
        event.preventDefault();

        if (index < totalItems - 1 && rest.handleSectionReorder) {
            rest.handleSectionReorder(sectionId, index, index + 1);
        }
    };

    // Check form conditions
    if (rest?.conditions && rest?.previewMode) {
        let conditionalChanges = formConditionsHandler(rest.conditions, rest.formValues || {}, data, rest.formElements, "", userInfo);
        if (conditionalChanges.includes("hide")) {
            return null
        }
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            onClick={handleElementClick}
            onDragStart={handleDragStart}
            onKeyDown={handleKeyDown}
            className={`
                block relative mb-2 transition-all duration-200 rounded-lg group
                ${isDragging ? 'opacity-50  z-50' : 'opacity-100 '}
                ${data.span && data.span !== "24" ? 'pr-0' : ''}
                bg-white 
                ${!isDragging ? 'cursor-pointer' : 'cursor-grabbing'}
            `}
            data-span={data.span || "24"}
        >
            {/* Drop zone before the first element in section */}
            {isFirstElement && (
                <SectionDropZone
                    position="before"
                    elementId={data.id}
                    sectionId={sectionId}
                    isDraggingWidget={isDraggingWidget}
                />
            )}

            <div className="absolute -top-3 right-2 flex gap-1 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ">
                {/* Move Up Button */}
                <Button
                    size="small"
                    type="text"
                    icon={<ArrowUpOutlined style={{ fontSize: '10px' }} />}
                    onClick={handleMoveUp}
                    disabled={index === 0}
                    className="h-5 w-5 p-0 flex items-center justify-center hover:bg-blue-100 disabled:opacity-30 bg-white shadow-sm border border-gray-100 rounded-md"
                    title={t('Move up')}
                    style={{ minWidth: '22px', minHeight: '22px' }}
                />

                {/* Move Down Button */}
                <Button
                    size="small"
                    type="text"
                    icon={<ArrowDownOutlined style={{ fontSize: '10px' }} />}
                    onClick={handleMoveDown}
                    disabled={index === totalItems - 1}
                    className="h-5 w-5 p-0 flex items-center justify-center hover:bg-blue-100 disabled:opacity-30 bg-white shadow-sm border border-gray-100 rounded-md"
                    title={t('Move down')}
                    style={{ minWidth: '22px', minHeight: '22px' }}
                />
            </div>

            {/* Drag handle indicator */}
            {isDragging && (
                <div className="absolute -top-2 -left-2 bg-blue-500 text-white px-2 py-1 rounded text-xs font-medium z-10">
                    {t('Moving...')}
                </div>
            )}

            <HoverWrapper
                data={data}
                sectionId={sectionId}
                isDragging={isDragging}
                isSortable={true}
                dragHandleProps={null}
                activeDragItem={data.formType === "section" ? activeDragItem : undefined}
                {...rest}
            />

            {/* Drop zone after each element in section */}
            <SectionDropZone
                position="after"
                elementId={data.id}
                sectionId={sectionId}
                isDraggingWidget={isDraggingWidget}
            />
        </div>
    );
});
SortableItem.displayName = "SortableItem";

// True only for the section the pointer has entered during the current drag.
// A field's two insertion zones are dnd-kit droppables, so mounting them for every
// field registered ~1000 droppables the moment a drag started - which is what froze
// the grab for ~2s on a large form. Now only the section actually being dragged over
// mounts its zones. Published by EnhancedDropZone (which already subscribes to
// dnd-kit for its own isOver) so individual sections do not each subscribe.
const SectionHoverContext = React.createContext(false);

// Enhanced Drop Zone Component with Tailwind CSS
// Presentational only. The droppable itself lives on Section's outer div - this
// component renders inside Collapse.Panel, and a collapsed panel is never rendered, so
// registering the drop target here meant a collapsed section accepted no drops at all
// and widgets silently landed at the form root.
const EnhancedDropZone = ({ isActive, children, isEmpty, isOver }) => {
    const { t } = useTranslation();

    return (
        <div
            className={`
                w-full rounded-lg transition-all duration-300 relative
                ${isEmpty ? 'min-h-[100px] p-5' : 'min-h-[60px]'}
                ${isActive || isOver ? 'cursor-copy' : 'cursor-default'}
            `}
        >
            {isEmpty ? (
                <div className="flex flex-col items-center gap-3 text-center py-4">
                    <PlusOutlined className={`text-3xl ${isOver ? 'text-blue-500' : 'text-gray-400'} transition-colors duration-200`} />
                    <div>
                        <div className={`text-sm font-medium ${isOver ? 'text-blue-500' : 'text-gray-600'} transition-colors duration-200`}>
                            {isOver ? "Drop element here" : "Empty Section"}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                            {t('Drag elements here to add to section')}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="relative">
                    {children}
                </div>
            )}

            {/* Bottom drop indicator line removed as we use plus icon drop zones now */}
        </div>
    );
};

// Above this many fields, sections in the EDITOR start collapsed. rc-collapse does not
// render an inactive panel's content, so on a large questionnaire this is the
// difference between mounting every field up front and mounting only what is open -
// it drives both how long the editor takes to appear and how much dnd-kit has to
// track during a drag. Smaller forms are untouched and still open expanded.
const LAZY_SECTION_FIELD_THRESHOLD = 120

const Section = (props) => {
    const { id, label, collapsible, defaultCollapse, children = [], activeDragItem, activeElement, ...rest } = props

    const formIsLarge = useMemo(
        () => getAllWidgets(rest.formElements || []).length > LAZY_SECTION_FIELD_THRESHOLD,
        [rest.formElements]
    )
    // The author's own "collapsed by default" setting still wins; this only adds
    // collapsing on forms big enough for it to matter.
    const startCollapsed = defaultCollapse || formIsLarge

    // A section is handed the whole form-value set (it is the only way to slice for its
    // children). Each child then gets only the values it can actually read, so a
    // keystroke re-renders the fields that depend on it rather than the whole section.
    const sliceRef = useRef(null)
    if (!sliceRef.current) sliceRef.current = createFormValuesSlicer()
    const formValuesFor = (data) => sliceRef.current(data, rest.formValues, rest.conditions)

    // The section's drop target lives out here, outside the Collapse, so it stays
    // registered while the section is collapsed.
    const { setNodeRef: setSectionDropRef, isOver, active } = useDroppable({
        id: `section-${id}`,
        data: {
            type: 'section',
            sectionId: id,
            accepts: ['form-element', 'section-element', 'widget']
        }
    });

    // Latched, not raw isOver: once a child insertion zone wins the collision the
    // section itself stops being `over`, and unmounting the zones at that moment would
    // make them flicker in and out. Cleared when the drag ends.
    const [entered, setEntered] = useState(false);
    useEffect(() => { if (isOver) setEntered(true); }, [isOver]);
    useEffect(() => { if (!active) setEntered(false); }, [active]);

    // Check if someone is dragging an element (use global active state)
    const isDraggingActive = !!activeDragItem;
    const isDraggingFromThisSection = rest.activeDragItem?.data?.current?.type === 'section-element' &&
        rest.activeDragItem?.data?.current?.parentSectionId === id;

    // Get the IDs of all items for sortable context
    const itemIds = useMemo(() => children.map(item => item.id), [children]);

    // Add a special class when something is being dragged over or dragged from this section
    const dragOverClass = "";
    const dragFromClass = isDraggingFromThisSection ? "section-drag-from" : "";

    return (
        <SectionHoverContext.Provider value={entered}>
        <div
            ref={setSectionDropRef}
            className={`
                form-section relative transition-all duration-300 
                ${dragOverClass} ${dragFromClass}
                ${isDraggingFromThisSection ? 'opacity-70' : 'opacity-100'}
            `}
            style={{
                borderColor: rest?.spaceData?.spaceColor ? rest.spaceData.spaceColor : "#722ed1",
            }}
        >
            {/* Drag overlay indicator */}
            {isDraggingActive && (
                <div className={`
                    absolute inset-0 rounded-[20px] pointer-events-none z-10 transition-all duration-300
                `}>
                </div>
            )}

            <Collapse
                collapsible="icon"
                bordered={false}
                defaultActiveKey={startCollapsed ? "" : "1"}
                destroyOnHidden
                expandIconPosition="end"
                className="section-collapse"
                style={{
                    border: "0px solid",
                    borderColor: rest?.spaceData?.spaceColor ? rest.spaceData.spaceColor : "#722ed1",
                    borderTop: "4px solid",
                    borderLeft: "1px solid",
                    borderRight: "1px solid",
                    borderBottom: "1px solid",
                    background: "#fff",
                    borderRadius: 20,
                    minHeight: "80px",
                    paddingBottom: "20px"
                }}
                expandIcon={({ isActive }) => (
                    <CaretLeftOutlined style={{ color: "#00000073" }} rotate={isActive ? 270 : 180} />
                )}
                data-id={id}
            >
                <Collapse.Panel
                    key={startCollapsed ? "3" : "1"}
                    className="collapse-panel-section"
                    header={
                        <Typography.Title className="m-0 p-0 text-left" level={4}>
                            {label}
                            {/* <span className="ml-2 text-xs font-normal text-gray-500">
                                ({children.length} {children.length === 1 ? 'element' : 'elements'})
                            </span>
                            {isDraggingActive && (
                                <span className={`
                                    ml-2 text-xs font-normal transition-colors duration-200 text-blue-500
                                `}>
                                    • Drop zone active
                                </span>
                            )} */}
                        </Typography.Title>
                    }
                    showArrow={collapsible ? collapsible : false}
                >
                    {/* Enhanced section content area */}
                    <EnhancedDropZone
                        isActive={isDraggingActive}
                        isOver={isOver}
                        isEmpty={children.length === 0}
                    >
                        {children.length > 0 && (
                            <div className="flex flex-wrap w-full items-start gap-0">
                                {/* Use only SortableContext without nested DndContext to prevent conflicts */}
                                <SortableContext
                                    items={itemIds}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {children.map((data, index) => {
                                        return (
                                            <SortableItem
                                                key={data.id}
                                                id={data.id}
                                                data={data}
                                                index={index}
                                                sectionId={id}
                                                totalItems={children.length}
                                                activeDragItem={activeDragItem}
                                                {...rest}
                                                formValues={formValuesFor(data)}
                                                isActive={activeElement?.id === data.id}
                                                activeElement={data.formType === "section" ? activeElement : undefined}
                                            />
                                        );
                                    })}
                                </SortableContext>
                            </div>
                        )}
                    </EnhancedDropZone>
                </Collapse.Panel>
            </Collapse>
        </div>
        </SectionHoverContext.Provider>
    )
}

// A section only needs to re-render when a value one of its own fields reads has
// actually changed. Without this every keystroke anywhere in the form re-rendered all
// 74 antd Collapse panels on a large questionnaire, which was the bulk of the
// remaining cost once the children themselves were memoized.
const sectionPropsEqual = (prev, next) => {
    const prevKeys = Object.keys(prev)
    const nextKeys = Object.keys(next)
    if (prevKeys.length !== nextKeys.length) return false
    for (const key of prevKeys) {
        if (key === "formValues") continue
        if (!Object.is(prev[key], next[key])) return false
    }
    const a = prev.formValues, b = next.formValues
    if (a === b) return true
    for (const key of sectionValueDeps(next.children, next.conditions)) {
        if (!Object.is(a?.[key], b?.[key])) return false
    }
    return true
}

export default React.memo(Section, sectionPropsEqual)
